import React, { Component, ChangeEvent } from 'react';
import { withTranslation } from 'react-i18next';

import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';
import { push, replace } from 'redux-first-history';

import {
    BasicTable,
    MobileTable,
    Typography,
    SearchInput,
    Pagination,
    EmptyState,
    DateDropdown,
    SimpleLoader,
    FiltersPopover,
    FilterIcon,
    PrimaryButton,
    Container,
    palette,
    BasicTableHeadersProps,
    ToggleButtonGroup,
} from '@dev/ee-components';

import styled from 'styled-components';
import { Link } from '@material-ui/core';

import { parse } from 'query-string';
import { map, truncate } from 'lodash-es';
import Skeleton from 'react-loading-skeleton';

import dayjs from '@api/services/tools/dayjs';

import { ShowDialog, HideDialog } from '@api/store/dialog/actions';
import { ShowDialogParams } from '@api/store/dialog/types';
import ExportModal from '../../../../ExportModal';
import { ExportLinksParams, LinkTrackingParams } from '@api/services/ElasticApi';
import { LinkTrackingState } from '@api/store/statistics/types';
import { LoadLinkTrackingListStart, FindLinksTracking, ExportLinks } from '@api/store/statistics/actions';
import SingleChannelProps from '../SingleChannelInterface';
import { ChannelsToggleBtnValues, ChannelsToggleBtnOptInterface } from '../../ChannelsInterface';
import { AccessLevel, CompressionFormat, ExportFileFormats } from '@dev/ee-api/ee-api-models';
import { ApplicationState as State } from '@api/store';
import { addParamToUrl } from '@api/services/tools/handleUrlParams';
import { getDateDropdownLabel } from '@api/services/tools/getDateDropdownValue';

import UpIcon from '../upIcon.svg';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { defaultDateDropdownOption, DateDropdownOptions, getDateDropdownOptionsConfig, getDateRangeData } from './config';
import { ManipulateType } from 'dayjs';
import { StatisticsPaths, pathsParams } from '@api/services/dictionaries/paths';

const Content = styled.div<{ isMobile: boolean }>`
    padding: 30px 0px;
    background-color: #fff;
    margin-top: ${(props): string => (props.isMobile ? '16px' : '32px')};
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 96px;
    width: 100%;
`;

const Header = styled.div`
    display: flex;
    margin-bottom: 32px;
    padding: 0px 48px;
    justify-content: space-between;
`;

const HeaderOptions = styled.div`
    display: flex;
    align-items: baseline;
`;

const ToggleButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    background-color: inherit;
    margin: 0 12px 24px 0;
`;

const TableWrapper = styled.div`
    & .MuiTableBody-root > tr:first-child {
        & th,
        td {
            border-top: none;
        }
    }
`;

const MobileHeader = styled.div`
    display: flex;
    margin-top: 32px;
    margin-bottom: 20px;
    justify-content: space-between;
`;

const MobileTableContainer = styled.div`
    margin-top: 24px;
`;

const FiltersContainer = styled.div`
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
    margin-left: 8px;
    padding: 11px 11px;
`;

const SearchContainer = styled.div`
    width: 278px;
`;

const MobileSearchContainer = styled.div`
    width: 100%;
    & > div {
        border: 1px solid ${palette.gray[3]};
    }
`;
const MobileFooter = styled.div`
    background: ${palette.basic.paper};
`;

const DATE_FORMAT = 'YYYY-MM-DD';
interface DateRangeValues {
    from: dayjs.Dayjs;
    to: dayjs.Dayjs;
}

interface QueryValues {
    from: dayjs.Dayjs;
    to: dayjs.Dayjs;
    daterange: DateDropdownOptions;
}

type DispatchProps = {
    LoadLinkTrackingListStart(params?: LinkTrackingParams);
    FindLinksTracking(params?: LinkTrackingParams);
    ExportLinks(params?: ExportLinksParams);
    ShowDialog: (params: ShowDialogParams) => void;
    HideDialog: () => void;
    Push(url: string): void;
    Replace(param: Record<string, string> | string): void;
};

type Props = DispatchProps & SingleChannelProps;

const dispatchProps = {
    LoadLinkTrackingListStart,
    FindLinksTracking,
    ExportLinks,
    ShowDialog,
    HideDialog,
    Push: push,
    Replace: replace,
};

const mapState = (state: State, ownProps: SingleChannelProps): SingleChannelProps => {
    return {
        ...ownProps,
        browser: state.browser,
        channels: state.channels,
        statistics: state.statistics,
        account: state.account,
    };
};
class ChannelLinkTracking extends Component<Props, LinkTrackingState> {
    constructor(props) {
        super(props);
        const { from, to, daterange } = this.getQueryValues();
        const { from: fromDefault, to: toDefault } = this.getDateRangeValues(defaultDateDropdownOption);
        const state: LinkTrackingState = {
            tableBtnValue: 'number',
            tabValue: 1,
            exportOptions: {
                from: null,
                to: null,
                channelName: this.props.channelName || safelyDecodeBase64Url(this.props.match.params.channelName),
                fileName: null,
                compressionFormat: CompressionFormat.Zip,
                fileFormat: ExportFileFormats.Csv,
            },
            from: from ?? fromDefault,
            to: to ?? toDefault,
            dateRangeValue: daterange,
        };
        this.state = state;
    }

    private getQueryValues(): QueryValues {
        const { location } = this.props;
        const query = parse(location.search);
        const dateDropdownOptionsConfig = getDateDropdownOptionsConfig();
        const from = query.from && dayjs(query.from as string).startOf('day');
        const to = query.to && dayjs(query.to as string).endOf('day');
        return {
            from,
            to,
            daterange: dateDropdownOptionsConfig[query.daterange as DateDropdownOptions]?.value || defaultDateDropdownOption,
        };
    }

    private getDateRangeValues(option: DateDropdownOptions): DateRangeValues {
        const dateDropdownConfig = getDateDropdownOptionsConfig();
        const to = dayjs().endOf('day');
        const { valueInUnit, unit } = dateDropdownConfig[option];
        const from = dayjs()
            .subtract(valueInUnit, unit as ManipulateType)
            .startOf('day');
        return { from, to };
    }

    public componentDidMount(): void {
        this.loadLinkTracking();
    }

    public componentDidUpdate(prevProps): void {
        if (prevProps.clickExport !== this.props.clickExport && this.props.clickExport) {
            this.openExportLinksModal();
        }
        this.props.disabledExport?.(!this.props.statistics.totalLinkCount);
    }

    private fileNameLinksRef = React.createRef<HTMLInputElement>();

    private loadLinkTracking(): void {
        const { LoadLinkTrackingListStart, location, match, channelName = safelyDecodeBase64Url(match.params.channelName) } = this.props;
        const query: any = parse(location.search);
        const params: LinkTrackingParams = {
            channelName,
            from: this.state.from.utc().startOf('day').toJSON(),
            to: this.state.to.utc().endOf('day').toJSON(),
        };

        if (query.page) {
            params.activePage = parseInt(query.page, 10);
        }

        if (query.search) {
            params.linkSearch = query.search;
        }

        if (query.perPage) {
            params.perPage = parseInt(query.perPage, 10);
        }
        LoadLinkTrackingListStart(params);
    }

    private addSearchQuery(toAdd: Record<string, any>): void {
        const { Replace } = this.props;
        const newPath = addParamToUrl(toAdd);
        Replace({ search: newPath });
    }

    private renderMobileHeader(): JSX.Element {
        const { t } = this.props;
        const query = parse(location.search);
        const startDate = query?.from?.toString();
        const endDate = query?.to?.toString();
        const dateDefaultValue = getDateDropdownLabel(DateDropdownOptions.Last7days);

        return (
            <MobileHeader>
                <MobileSearchContainer>{this.searchInput()}</MobileSearchContainer>
                <FiltersContainer>
                    <FiltersPopover
                        onApply={(obj, calendarFrom, calendarTo): void => {
                            this.mobileFiltersApply(obj, calendarFrom, calendarTo);
                        }}
                        tabs={[
                            {
                                label: t('daterange'),
                                defaultValue: dateDefaultValue,
                                options: getDateRangeData(true),
                                type: 'date',
                                startDate,
                                endDate,
                            },
                            {
                                label: t('valuerange'),
                                defaultValue: 'number',
                                options: [
                                    {
                                        label: t('count'),
                                        value: 'number',
                                    },
                                    {
                                        label: t('percentage'),
                                        value: 'percent',
                                    },
                                ],
                                type: 'radio',
                            },
                        ]}
                        title={t('filters')}
                        translations={{
                            apply: t('apply'),
                            close: t('close'),
                        }}
                    >
                        <FilterIcon color={palette.gray[6]} width={20} height={20} />
                    </FiltersPopover>
                </FiltersContainer>
            </MobileHeader>
        );
    }

    private mobileFiltersApply(filtersObj, newFrom, newTo): void {
        const dateValue = filtersObj[0] as DateDropdownOptions;
        const newTableBtnValue = filtersObj[1];
        let from;
        let to;

        if (dateValue === DateDropdownOptions.Calendar) {
            from = dayjs(newFrom).startOf('day');
            to = dayjs(newTo).endOf('day');
        } else {
            const { from: fromDate, to: toDate } = this.getDateRangeValues(dateValue);
            from = fromDate;
            to = toDate;
        }

        this.addSearchQuery({ from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT), daterange: dateValue, page: 1 });
        this.setState({ from, to, dateRangeValue: dateValue }, (): void => {
            this.loadLinkTracking();
        });

        //check Value range tab
        if (newTableBtnValue !== this.state.tableBtnValue) {
            this.setState({ tableBtnValue: newTableBtnValue });
        }
    }

    private renderHeader(): JSX.Element {
        const isMobile = this.isMobile();
        return (
            <Header>
                <HeaderOptions>
                    {isMobile ? null : this.statisticsButtons()}
                    {this.renderDateRange()}
                </HeaderOptions>
                <SearchContainer>{this.searchInput()}</SearchContainer>
            </Header>
        );
    }

    private statisticsButtons(): JSX.Element {
        const toggleBtnOptions: ChannelsToggleBtnOptInterface[] = [
            {
                label: '#',
                value: ChannelsToggleBtnValues.Number,
            },
            {
                label: '%',
                value: ChannelsToggleBtnValues.Percent,
            },
        ];

        return (
            <ToggleButtonWrapper>
                <ToggleButtonGroup
                    defaultValue={ChannelsToggleBtnValues.Number}
                    size="small"
                    options={toggleBtnOptions}
                    onChange={(format) => {
                        this.setState({
                            tableBtnValue: format,
                        });
                    }}
                />
            </ToggleButtonWrapper>
        );
    }

    private searchInput(): JSX.Element {
        const { t, location } = this.props;
        const queryParams = parse(location.search);

        return (
            <SearchInput
                defaultValue={queryParams.search}
                placeholder={t('searchforlinks')}
                onChange={this.findLinks.bind(this)}
                onDelete={this.findLinks.bind(this)}
                type="text"
            />
        );
    }

    private hasDisabledContactStorage(): boolean {
        const { GeneralInfo } = this.props?.account || {};
        return !GeneralInfo?.AccountAccessLevel?.includes?.(AccessLevel.ViewContacts);
    }

    private renderTable(): JSX.Element {
        const headers = this.getTableHeaders();
        const { statistics, t } = this.props;

        const columnsWidth: Record<string, string> = {
            // trackedLink: '65%',
            uniqueclicks: '10%',
            count: '10%',
            viewContacts: '15%',
        };

        const SkeletonTemplate = {
            key: 'skeleton',
            trackedLink: <Skeleton />,
            uniqueclicks: <Skeleton />,
            count: <Skeleton />,
            viewContacts: <Skeleton />,
        };
        const SkeletonRows = [...new Array(statistics.linkTrackingPerPage)].map((_) => SkeletonTemplate);

        return (
            <BasicTable
                rows={statistics.linktrackingLoading ? SkeletonRows : this.getTableRows()}
                columnWidth={columnsWidth}
                headers={headers}
                isEmpty={!statistics.linktrackingLoading && !statistics.totalLinkCount}
                emptyStateProps={{ text: t('thereisnodatatodisplay') }}
            />
        );
    }

    private getTableHeaders(): BasicTableHeadersProps[] {
        const { t } = this.props;
        const isNumeric = this.state.tableBtnValue === 'number';
        const hasDisabledContactStore = this.hasDisabledContactStorage();

        const headers: BasicTableHeadersProps[] = [
            { id: 'trackedLink', disablePadding: true, label: t('trackedlink') },
            {
                id: 'uniqueclicks',
                disablePadding: false,
                label: isNumeric && !hasDisabledContactStore ? t('uniqueclicks') : '',
                columnAlign: 'right',
            },
            { id: 'count', disablePadding: false, label: t('clicks'), columnAlign: 'right' },
            ...(!hasDisabledContactStore ? [{ id: 'viewContacts', disablePadding: false, label: '', columnAlign: 'right' as const }] : []),
        ];

        return headers;
    }

    private getTableRows(): any[] {
        const {
            statistics: { linkTrackingList },
            statistics,
            t,
        } = this.props;
        const isNumeric = this.state.tableBtnValue === 'number';
        const linkTrackingArr = [];
        const hasDisabledContactStore = this.hasDisabledContactStorage();
        if (statistics.totalLinkCount === 0) {
            return [];
        }
        map(linkTrackingList, (link): void => {
            const linkLabel = truncate(link.Link, {
                length: 90,
            });
            const count = isNumeric ? link.Clicks : link.Percent;
            linkTrackingArr.push({
                trackedLink: <Typography variant="caption">{linkLabel}</Typography>,
                count: <Typography variant="caption">{count}</Typography>,
                uniqueclicks: isNumeric && !hasDisabledContactStore && <Typography variant="caption">{link.UniqueClicks}</Typography>,
                ...(!hasDisabledContactStore && {
                    viewContacts: (
                        <Typography variant="body1">
                            <Link onClick={this.handleClickContactView.bind(this, link.Link)} variant="body1">
                                {t('viewcontacts')}
                            </Link>
                        </Typography>
                    ),
                }),
            });
        });
        return linkTrackingArr;
    }

    private getLinkDateParams(): string {
        const { from, to, dateRangeValue } = this.state;

        return `daterange=${dateRangeValue}&from=${from.format(DATE_FORMAT)}&to=${to.format(DATE_FORMAT)}`;
    }

    private handleClickContactView(link): void {
        const {
            match,
            Push,
            channelName = match.params.channelName,
            singleChannelPath = StatisticsPaths.StatisticsChannelLinkTrackingContacts,
        } = this.props;

        Push(
            `${singleChannelPath.replace(pathsParams.channelName, channelName)}?link=${safelyEncodeBase64Url(
                link
            )}&${this.getLinkDateParams()}`
        );
    }

    private renderMobileTable(): JSX.Element {
        const { statistics, t } = this.props;

        const header = this.getMobileTableHeaders();
        const rows = this.getMobileTableRows();

        if (!statistics.totalLinkCount) {
            return (
                <div style={{ marginTop: '16px' }}>
                    <EmptyState background="#fff" text={t('thereisnodatatodisplay')} />
                </div>
            );
        }

        return (
            <MobileTableContainer>
                <MobileTable
                    isWrapTextEnabled
                    headers={header}
                    rows={rows}
                    onClickButton={(e, rows): void => {
                        this.handleClickContactView(rows.trackedLink);
                    }}
                    expandedRows={
                        {
                            trackedLink: true,
                        } as any
                    }
                />
            </MobileTableContainer>
        );
    }

    private getMobileTableHeaders(): string[] {
        const { t } = this.props;
        const isNumeric = this.state.tableBtnValue === 'number';
        if (isNumeric) {
            return [t('trackedlink'), t('uniqueclicks'), t('clicks')];
        }
        return [t('trackedlink'), t('clicks')];
    }

    private getMobileTableRows(): any[] {
        const { linkTrackingList, linktrackingLoading, linkTrackingPerPage } = this.props.statistics;
        const isNumeric = this.state.tableBtnValue === 'number';

        if (!linkTrackingList) {
            return [];
        }

        if (isNumeric) {
            const SkeletonTemplate = {
                trackedLink: <Skeleton />,
                uniqueclicks: <Skeleton />,
                count: <Skeleton />,
            };
            const SkeletonRows = [...new Array(linkTrackingPerPage)].map((_) => SkeletonTemplate);

            const rows = linkTrackingList.map((link) => {
                return {
                    trackedLink: link.Link,
                    uniqueclicks: link.UniqueClicks,
                    count: isNumeric ? link.Clicks : link.Percent,
                };
            });

            return linktrackingLoading ? SkeletonRows : rows;
        }

        const SkeletonTemplate = {
            trackedLink: <Skeleton />,
            count: <Skeleton />,
        };
        const SkeletonRows = [...new Array(linkTrackingPerPage)].map((_) => SkeletonTemplate);

        const rows = linkTrackingList.map((link) => {
            return {
                trackedLink: link.Link,
                count: isNumeric ? link.Clicks : link.Percent,
            };
        });

        return linktrackingLoading ? SkeletonRows : rows;
    }

    private findLinks(ev: ChangeEvent<HTMLInputElement>): void {
        const { FindLinksTracking, statistics, match, channelName = safelyDecodeBase64Url(match.params.channelName) } = this.props;
        const { linkTrackingPerPage } = statistics;
        const { from, to } = this.state;

        FindLinksTracking({
            linkSearch: ev?.currentTarget?.value || '',
            channelName,
            from: from.utc().startOf('day').format(),
            to: to.utc().endOf('day').format(),
            perPage: linkTrackingPerPage,
        });
    }

    private changePage(page: number, perPage: number): void {
        const { LoadLinkTrackingListStart, match, channelName = safelyDecodeBase64Url(match.params.channelName) } = this.props;
        const { from, to } = this.state;

        LoadLinkTrackingListStart({
            channelName,
            activePage: page,
            from: from.utc().startOf('day').format(),
            to: to.utc().endOf('day').format(),
            perPage,
        });
    }

    private changeExportFileFormat(data: { value: ExportFileFormats; label: string }): void {
        const { exportOptions } = this.state;

        exportOptions.fileFormat = data.value;

        this.setState({ exportOptions });
    }

    private changeExportCompression(enabled: boolean): void {
        const { exportOptions } = this.state;

        exportOptions.compressionFormat = enabled ? CompressionFormat.Zip : CompressionFormat.None;

        this.setState({ exportOptions });
    }

    private exportLinks(): void {
        const { ExportLinks, match, channelName = safelyDecodeBase64Url(match.params.channelName) } = this.props;
        const { exportOptions, from, to } = this.state;
        const fileName = this.fileNameLinksRef.current.value || 'export';
        const fileFormat: ExportFileFormats = exportOptions.fileFormat;

        const exportParams = {
            from: from.utc().startOf('day').format(),
            to: to.utc().endOf('day').format(),
            fileName,
            fileFormat,
            channelName,
            compressionFormat: exportOptions.compressionFormat,
        };

        ExportLinks(exportParams);
    }

    private openExportLinksModal(): void {
        const { ShowDialog, t } = this.props;
        const { exportOptions } = this.state;

        ShowDialog({
            title: t('exportlinks'),
            body: (
                <ExportModal
                    onCompressionChange={this.changeExportCompression.bind(this)}
                    onFileFormatChange={this.changeExportFileFormat.bind(this)}
                    inputRef={this.fileNameLinksRef}
                    inputValue={exportOptions.fileFormat}
                />
            ),
            confirmLabel: t('export'),
            cancelLabel: t('cancel'),
            onClickConfirm: this.exportLinks.bind(this),
        });
    }
    private isMobile(): boolean {
        const { browser } = this.props;
        return browser?.lessThan?.hd;
    }

    private setDatesBasedOnCalendar(fromTime: Date, toTime: Date): void {
        const from = dayjs(fromTime).startOf('day');
        const to = dayjs(toTime).endOf('day');

        this.addSearchQuery({
            from: from.format(DATE_FORMAT),
            to: to.format(DATE_FORMAT),
            daterange: DateDropdownOptions.Calendar,
            page: 1,
        });
        this.setState({ from, to, dateRangeValue: DateDropdownOptions.Calendar }, (): void => {
            this.loadLinkTracking();
        });
    }

    private renderDateRange(): JSX.Element {
        const dateRangeData = getDateRangeData(false);
        let startDate, endDate;
        if (this.state.dateRangeValue === DateDropdownOptions.Calendar) {
            startDate = this.state.from.format();
            endDate = this.state.to.format();
        }
        const dateDropdownValue = getDateDropdownLabel(DateDropdownOptions.Last7days);

        return (
            <DateDropdown
                startDate={startDate}
                endDate={endDate}
                calendarOnChange={this.setDatesBasedOnCalendar.bind(this)}
                value={dateDropdownValue}
                options={dateRangeData}
                type="input"
                returnRawData
                placement="bottom-end"
                buttonVariant="tertiary"
                onItemClick={(e, obj): void => {
                    const dateOptionValue = obj.value as DateDropdownOptions;
                    const { from, to } = this.getDateRangeValues(dateOptionValue);
                    this.addSearchQuery({
                        from: from.format(DATE_FORMAT),
                        to: to.format(DATE_FORMAT),
                        daterange: dateOptionValue,
                        page: 1,
                    });
                    this.setState({ from, to, dateRangeValue: dateOptionValue }, (): void => {
                        this.loadLinkTracking();
                    });
                }}
            />
        );
    }

    private renderFooter(): JSX.Element {
        const {
            statistics: { totalLinkCount, linkTrackingActivePage, linkTrackingPerPage },
        } = this.props;

        return (
            <Pagination
                dataLength={totalLinkCount}
                perPage={linkTrackingPerPage}
                activePage={linkTrackingActivePage}
                siblingCount={1}
                boundaryCount={1}
                isRecalculatingActivePage
                handlePaginationChange={this.changePage.bind(this)}
                perPageOptions={[
                    { label: '5', value: 5 },
                    { label: '10', value: 10 },
                    { label: '15', value: 15 },
                    { label: '20', value: 20 },
                    { label: '25', value: 25 },
                ]}
            />
        );
    }

    private renderLoadingState(): JSX.Element {
        const displayContainer = {
            display: 'flex',
            height: 'calc(100vh - 200px)',
        };

        return (
            <Container style={displayContainer}>
                <LoadingWrapper>
                    <SimpleLoader />
                </LoadingWrapper>
            </Container>
        );
    }

    public render(): JSX.Element {
        const {
            t,
            statistics: { totalLinkCount, linkTrackingList },
        } = this.props;
        const isMobile = this.isMobile();
        if (isMobile) {
            if (!linkTrackingList && totalLinkCount !== 0) {
                return this.renderLoadingState();
            }

            return (
                <>
                    {this.renderMobileHeader()}
                    <PrimaryButton
                        id="exportlinks"
                        onClick={this.openExportLinksModal.bind(this)}
                        disabled={!totalLinkCount}
                        size="medium"
                        text={t('exportlinks')}
                        variant="text"
                        startIcon={<UpIcon />}
                    />
                    {this.renderMobileTable()}
                    {!!totalLinkCount && <MobileFooter>{this.renderFooter()}</MobileFooter>}
                </>
            );
        } else {
            return (
                <Content isMobile={isMobile}>
                    {this.renderHeader()}
                    <TableWrapper>{this.renderTable()}</TableWrapper>
                    {!!totalLinkCount && this.renderFooter()}
                </Content>
            );
        }
    }
}

export default compose(
    withRouter,
    connect<SingleChannelProps, DispatchProps>(mapState, dispatchProps),
    withTranslation()
)(ChannelLinkTracking);
